import { createEffect, createStore, sample } from "effector"
import { $callcenterService, $userService } from "@callcenter/effector-services"
import { $whiteLabel } from "@callcenter/white-label"

import { SUPPORT_CHAT_COLOR, SUPPORT_CHAT_HEIGHT, SUPPORT_CHAT_WIDTH } from "./support-chat.constants"
import {
  getIsExcludedApp,
  getIsPremium,
  getIsTrial,
  getSupportChatAnalyticsSettings,
  isShowSupportWebchat,
  mountSupportChat
} from "./support-chat.helpers"
import type { MountSupportChatPayload, SupportChatSettings } from "./support-chat.types"

const mountSupportChatFx = createEffect<MountSupportChatPayload, void>(mountSupportChat)

const $isMounted = createStore(false).on(mountSupportChatFx.done, () => true)
const $isExcludedApp = createStore(getIsExcludedApp())

sample({
  source: {
    resources: $callcenterService.$resources,
    callcenter: $callcenterService.$settings,
    user: $userService.$userSettings,
    permissions: $userService.$permissions,
    whiteLabel: $whiteLabel.$whiteLabelSettings,
    isMounted: $isMounted,
    isExcludedApp: $isExcludedApp
  },
  filter: isShowSupportWebchat,
  fn: ({ resources, callcenter, user, permissions, whiteLabel }): MountSupportChatPayload => {
    const isTrialChat = getIsTrial(user, callcenter)
    const isPremiumChatGranted = getIsPremium(user, callcenter)
    const isTicketsGranted = permissions.has("access_support_center")

    const baseSettings = {
      ...getSupportChatAnalyticsSettings(user, callcenter),
      token: user.premium_support_token ?? "",
      wss: resources.premium_support_wss_url ?? "",
      header: `${whiteLabel.brandName} support team`,
      color: SUPPORT_CHAT_COLOR,
      width: SUPPORT_CHAT_WIDTH,
      height: SUPPORT_CHAT_HEIGHT,
      brand_name: whiteLabel.brandName,
      brand_domain_name: whiteLabel.brandDomainName
    }

    const settings: SupportChatSettings = isTrialChat
      ? { ...baseSettings, self_service_trial: true }
      : {
          ...baseSettings,
          support_center: isTicketsGranted,
          premium_support: isPremiumChatGranted,
          premium_support_admin: user.view_all_support_chats ?? false,
          premium_support_enabled: callcenter.premium_support_enabled ?? false
        }

    return {
      scriptUrl: resources.web_chat_support_script_url!,
      stylesUrl: resources.web_chat_support_styles_url!,
      settings
    }
  },
  target: mountSupportChatFx
})
